import React, { useState, useEffect } from 'react';
import { partnerUserService } from '../services/api';

const EMPTY_FORM = {
  email: '',
  given_name: '',
  family_name: ''
};

export default function ManageTeam() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = () => {
    setLoading(true);
    partnerUserService.listCompanyUsers()
      .then(data => setUsers(data.users || []))
      .catch(() => setMessage({ type: 'error', text: 'Failed to load team members' }))
      .finally(() => setLoading(false));
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) return;

    setInviting(true);
    setMessage(null);

    try {
      const result = await partnerUserService.invite(form);
      setMessage({
        type: 'success',
        text: `Invited ${form.email}. Temporary password: ${result.temp_password}`
      });
      setForm(EMPTY_FORM);
      loadUsers(); // Refresh list
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to invite user';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="manage-team">
      <div className="manage-team-header">
        <h2>Manage Team</h2>
        <p className="subtitle">Invite salespeople to your team</p>
      </div>

      {/* Invite form */}
      <div className="invite-panel">
        <h3>Invite Salesperson</h3>
        {message && (
          <div className={`message ${message.type}`}>
            {message.text}
            <button onClick={() => setMessage(null)}>×</button>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder="salesperson@company.com"
              />
            </div>
            <div className="form-group">
              <label>First Name</label>
              <input
                name="given_name"
                value={form.given_name}
                onChange={handleChange}
                placeholder="First name"
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                name="family_name"
                value={form.family_name}
                onChange={handleChange}
                placeholder="Last name"
              />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={inviting}>
            {inviting ? 'Sending Invite…' : 'Send Invite'}
          </button>
        </form>
      </div>

      {/* Team members list */}
      <div className="team-panel">
        <h3>Team Members ({users.length})</h3>
        {loading ? (
          <div className="loading">Loading…</div>
        ) : users.length === 0 ? (
          <div className="no-data">No team members yet</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.username}>
                    <td>{u.email}</td>
                    <td>{[u.given_name, u.family_name].filter(Boolean).join(' ') || '—'}</td>
                    <td>
                      <span className="tag" style={{ fontSize: '0.75rem' }}>
                        {u.role === 'manager' ? 'Manager' : u.role === 'salesperson' ? 'Salesperson' : 'Partner Admin'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${u.status === 'CONFIRMED' ? 'synced' : 'pending'}`}>
                        {u.status}
                      </span>
                    </td>
                    <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
